import { zonedTimeToUtc } from 'date-fns-tz'

export type PreferredTimeWindow = 'morning' | 'afternoon'

/**
 * Preferred time windows are stored as UTC timestamps with specific hours (8 or 12)
 * to encode morning vs afternoon slots, evaluated in the property's timezone.
 */
export const PREFERRED_TIME_WINDOW_HOURS: Record<PreferredTimeWindow, number> = {
  morning: 8,
  afternoon: 12,
}

const getTimezone = (timezone?: string): string => {
  try {
    return timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York'
  } catch {
    return 'America/New_York'
  }
}

export const getPreferredTimeWindow = (
  timestamp?: number | null,
  timezone?: string
): PreferredTimeWindow | undefined => {
  if (!timestamp) return undefined

  const date = new Date(timestamp * 1000)
  const tz = getTimezone(timezone)
  const hour = parseInt(date.toLocaleString('en-US', { hour: 'numeric', hour12: false, timeZone: tz }))

  if (hour === PREFERRED_TIME_WINDOW_HOURS.morning) return 'morning'
  if (hour === PREFERRED_TIME_WINDOW_HOURS.afternoon) return 'afternoon'
  return undefined
}

export const getPreferredTimeWindowLabel = (timestamp?: number | null, timezone?: string): string => {
  const window = getPreferredTimeWindow(timestamp, timezone)
  if (window === 'morning') return 'Morning (8am - 12pm)'
  if (window === 'afternoon') return 'Afternoon (12pm - 5pm)'
  return '-'
}

/**
 * Combine a calendar date (year/month/day, as picked in the UI) with a preferred
 * time window into a single UTC timestamp, honoring the property's timezone.
 * If window is undefined, the date is kept as-is so date-only edits keep working.
 */
export const combinePreferredDateAndWindow = (
  date: Date | null,
  window: PreferredTimeWindow | undefined,
  timezone?: string
): number | null => {
  if (!date) return null

  const tz = getTimezone(timezone)

  if (!window) {
    return Math.floor(date.getTime() / 1000)
  }

  const hour = PREFERRED_TIME_WINDOW_HOURS[window]
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const dateStr = `${year}-${month}-${day}T${String(hour).padStart(2, '0')}:00:00`

  const utcDate = zonedTimeToUtc(dateStr, tz)
  return Math.floor(utcDate.getTime() / 1000)
}
